import type { BuilderPublicPackagesUrls } from '@wix/thunderbolt-symbols'
import type { IBuilderComponentSdkLoader } from '../../types'

// Extend the worker global scope to include es-module-shims methods
declare const self: DedicatedWorkerGlobalScope & {
	importShim: {
		addImportMap: (map: { imports: Record<string, string> }) => void
		(url: string): Promise<any>
	}
}

let esModuleShimsInitialized = false
let currentImportMapSdkUrls: Record<string, string> = {}
let importMapNeedsUpdate = false

/**
 * Updates the current import map URLs and marks that the import map needs to be updated.
 * Should be called when new bootstrap data is available (e.g., on page navigation).
 */
export function updateClientImportMap(builderComponentsImportMapSdkUrls: Record<string, string>): void {
	currentImportMapSdkUrls = { ...builderComponentsImportMapSdkUrls }
	importMapNeedsUpdate = true
}

/**
 * Initializes es-module-shims in the worker environment.
 * This enables ES module import map support for resolving external dependencies.
 * Dependency hierarchy:
 * - @wix/builder-state: No dependencies
 * - @wix/builder-velo: Depends on @wix/builder-state
 * - @wix/builder-wix-components: Depends on both @wix/builder-velo and @wix/builder-state
 * - SDK itself: Depends on all three
 */
const ensureEsModuleShimsInitialized = async (
	builderPublicPackagesUrls: BuilderPublicPackagesUrls,
	shouldDisableBuilderWixComponents: boolean
): Promise<void> => {
	// Only update if not initialized yet or if import map was marked for update
	if (esModuleShimsInitialized && !importMapNeedsUpdate) {
		return
	}

	const importMap = {
		'@wix/builder-state': builderPublicPackagesUrls.esm['builder-state'],
		'@wix/builder-velo': builderPublicPackagesUrls.esm['builder-velo'],
		...(!shouldDisableBuilderWixComponents && {
			'@wix/builder-wix-components': builderPublicPackagesUrls.esm['builder-wix-components-sdks'],
		}),
		...currentImportMapSdkUrls,
	}

	try {
		if (!esModuleShimsInitialized) {
			// Load es-module-shims polyfill for import maps in workers
			// Served from Wix static CDN (thunderbolt-shims-static)
			const esModuleShimsUrl =
				'https://static.parastorage.com/services/thunderbolt-shims-static/1.0.0/es-module-shims.js'

			// Import the es-module-shims library into the worker
			importScripts(esModuleShimsUrl)
			esModuleShimsInitialized = true
		}

		// Set up or update the import map for resolving builder component dependencies
		self.importShim.addImportMap({
			imports: importMap,
		})

		importMapNeedsUpdate = false

		console.log('es-module-shims import map updated:', importMap)
	} catch (error) {
		console.error('Failed to initialize es-module-shims:', error)
		esModuleShimsInitialized = false
		throw new Error(`ES module shims initialization failed: ${error.message}`)
	}
}

/**
 * Creates a client-side builder component SDK loader that works in web worker environments.
 * Uses es-module-shims to handle ESM dependencies through import maps.
 *
 * @returns IBuilderComponentSdkLoader implementation
 */
export const createClientBuilderComponentSdkLoader = (
	builderPublicPackagesUrls: BuilderPublicPackagesUrls,
	shouldDisableBuilderWixComponents: boolean = false
): IBuilderComponentSdkLoader => {
	return {
		loadSdk: async (url: string): Promise<any> => {
			try {
				// Initialize/update es-module-shims with current import map
				await ensureEsModuleShimsInitialized(builderPublicPackagesUrls, shouldDisableBuilderWixComponents)

				// Use importShim instead of dynamic import to resolve dependencies
				// This allows the import map to properly resolve external dependencies
				const module = await self.importShim(url)

				// Return the default export (standard for ESM modules)
				if (module.default) {
					return module.default
				}

				// Fallback: if no default export, return the entire module
				return module
			} catch (error) {
				const errorMessage = `Failed to load Builder SDK from ${url}: ${error.message}`
				console.error(errorMessage, error)

				// ESM with default export - falling back to import component with no dependencies (support not Piler Components)
				return import(/* webpackIgnore: true */ url).then((sdk) => sdk.default)
			}
		},
	}
}
